import { useCallback, useEffect, useState } from "react"
import { toast } from "react-toastify"

import { useTranslation } from "react-i18next"

import { ModalConfirmAction } from "components/modals/ModalConfirmAction"
import { Column, DataTable } from "components/tables/DataTable"

import { seoService } from "services/seo/seo.service"
import { SeoOverrideGroup } from "services/seo/type"

import { useBrandSite } from "../BrandSiteContext"
import { AsyncList, dangerButtonClass, FilterField, IndexFlag, InfoBox, inputClass, LocaleChips, PathText, primaryButtonClass } from "../CmsUi"
import { formatDateTime } from "../helpers/format"
import { RouteSeoModal, RouteSeoState } from "../RouteSeoModal"

/**
 * Routes the frontend renders itself — /casino, /sportsbook, the policy pages.
 * One row per route, not per language: the path is the same everywhere.
 */
export function RouteSeoTab() {
  const { t } = useTranslation("translation", { useSuspense: false })
  const site = useBrandSite()

  const [groups, setGroups] = useState<SeoOverrideGroup[]>([])
  const [loading, setLoading] = useState(false)
  const [search, setSearch] = useState("")
  const [formState, setFormState] = useState<RouteSeoState>({ kind: "none" })
  const [deleteCandidate, setDeleteCandidate] = useState<SeoOverrideGroup | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setGroups((await seoService.listOverrideGroups()) ?? [])
    setLoading(false)
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const confirmDelete = async (group: SeoOverrideGroup) => {
    if (await seoService.deleteOverridePath(group.path)) {
      toast.success(t("seo.routeDeleted", { defaultValue: "Route override deleted" }) as string)
      load()
    }
  }

  const needle = search.trim().toLowerCase()
  const visible = groups.filter(
    (group) => !needle || group.path.toLowerCase().includes(needle) || (group.title ?? "").toLowerCase().includes(needle)
  )

  const columns: Column<SeoOverrideGroup>[] = [
    {
      header: t("seo.col.path", { defaultValue: "Route" }),
      render: (group) =>
        site.origin ? (
          <a
            href={site.url(site.defaultLocale, group.path || "/")}
            target="_blank"
            rel="noreferrer"
            onClick={(event) => event.stopPropagation()}
          >
            <PathText className="underline decoration-dotted underline-offset-2">{group.path || "/"}</PathText>
          </a>
        ) : (
          <PathText>{group.path || "/"}</PathText>
        )
    },
    {
      header: t("seo.col.title", { defaultValue: "Title" }),
      wrap: true,
      render: (group) => <span className="text-sm">{group.title || <span className="opacity-50">—</span>}</span>
    },
    {
      header: t("cmsSeo.col.index", { defaultValue: "Index" }),
      hideBelow: "md",
      render: (group) => <IndexFlag indexed={group.indexed} />
    },
    {
      header: t("cmsSeo.col.locales", { defaultValue: "Locales" }),
      hideBelow: "lg",
      render: (group) => (
        <LocaleChips
          locales={group.locales}
          missing={group.missing_locales}
          onCreate={(locale) => setFormState({ kind: "edit", path: group.path, locale })}
        />
      )
    },
    {
      header: t("cmsSeo.col.updated", { defaultValue: "Updated" }),
      hideBelow: "xl",
      render: (group) => (
        <span className="text-xs text-gray-500 dark:text-gray-400">{formatDateTime(group.updated_at)}</span>
      )
    },
    {
      header: t("common.actions", { defaultValue: "Actions" }),
      align: "right",
      render: (group) => (
        <div className="flex justify-end gap-2">
          <button
            type="button"
            className={primaryButtonClass}
            onClick={(event) => {
              event.stopPropagation()
              setFormState({ kind: "edit", path: group.path })
            }}
          >
            {t("common.edit", { defaultValue: "Edit" })}
          </button>
          <button
            type="button"
            className={dangerButtonClass}
            onClick={(event) => {
              event.stopPropagation()
              setDeleteCandidate(group)
            }}
          >
            {t("common.delete", { defaultValue: "Delete" })}
          </button>
        </div>
      )
    }
  ]

  return (
    <div className="flex flex-col gap-5 p-4 sm:p-5">
      <div className="flex flex-wrap items-end gap-4">
        <FilterField label={t("common.search", { defaultValue: "Search" })} grow>
          <input
            className={inputClass}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={t("seo.searchRoutes", { defaultValue: "Search by route or title…" }) as string}
          />
        </FilterField>

        <button
          type="button"
          className={`${primaryButtonClass} w-full sm:w-auto`}
          onClick={() => setFormState({ kind: "create" })}>
          {t("seo.new", { defaultValue: "+ Add route" })}
        </button>
      </div>

      <InfoBox>
        {t("seo.subtitle", {
          defaultValue:
            "Title, description, H1 and indexing for routes the site renders itself — /casino, /sportsbook, /vip and the policy pages. Authored pages live on the Pages tab instead."
        })}
        <div className="mt-2">
          {t("seo.localeFallback", {
            defaultValue:
              "One row per route, with a language bar inside it. A language with no override of its own serves the English one, so you only add the languages that differ."
          })}
        </div>
        <div className="mt-2">
          {t("seo.sportsbookFirstNotice", {
            defaultValue:
              "On a sportsbook-first brand the sportsbook is the home page, so override the home route rather than /sportsbook — /sportsbook does not exist there."
          })}
        </div>
      </InfoBox>

      <AsyncList
        loading={loading}
        isEmpty={visible.length === 0}
        emptyText={t("seo.empty", { defaultValue: "No overrides yet — every built-in route uses the brand defaults." }) as string}
      >
        <DataTable
          rows={visible}
          rowKey={(group) => group.path}
          columns={columns}
          onRowClick={(group) => setFormState({ kind: "edit", path: group.path })}
        />
      </AsyncList>

      <RouteSeoModal
        state={formState}
        onClose={() => {
          setFormState({ kind: "none" })
          load()
        }}
        onSaved={load}
      />

      <ModalConfirmAction
        data={deleteCandidate}
        handler={() => setDeleteCandidate(null)}
        text={
          deleteCandidate
            ? (t("seo.confirmDeleteRoute", {
                defaultValue:
                  'Delete the override for "{{path}}" in all {{count}} language(s)? The route falls back to the brand defaults.',
                path: deleteCandidate.path || "/",
                count: deleteCandidate.locales.length
              }) as string)
            : ""
        }
        actionText={t("common.delete", { defaultValue: "Delete" })}
        actionClassName="btn-error"
        actionHandler={confirmDelete}
      />
    </div>
  )
}
